import React, { useState, useEffect } from 'react';
import UserService from '../../services/UserService';

/**
 * Hook for fetching messages with proper state management
 */
const useMessages = (filters, sorting, axiosInstance) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [relatiesVerzend, setRelatiesVerzend] = useState([]);
  const [relatiesOntvangt, setRelatiesOntvangt] = useState([]);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!axiosInstance) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        // Add filter parameters
        if (filters.debiNr) {
          params.append('Filters.DebiNr', filters.debiNr);
        }
        if (filters.abonnementId) {
          params.append('Filters.AboId', filters.abonnementId);
        }
        if (filters.interfaceId) {
          params.append('Filters.InterfaceId', filters.interfaceId);
        }
        if (filters.startDate) {
          params.append('Filters.StartDate', filters.startDate);
        }
        if (filters.endDate) {
          params.append('Filters.EndDate', filters.endDate);
        }
        if (filters.ontvangerGLN) {
          params.append('Filters.OntvangerGLN', filters.ontvangerGLN);
        }
        if (filters.verzenderGLN) {
          params.append('Filters.VerzenderGLN', filters.verzenderGLN);
        }
        if (filters.ontvangerRelaNr) {
          params.append('Filters.OntvangerRelaNr', filters.ontvangerRelaNr);
        }
        if (filters.verzenderRelaNr) {
          params.append('Filters.VerzenderRelaNr', filters.verzenderRelaNr);
        }
        if (filters.functioneelId) {
          params.append('Filters.FunctioneelId', filters.functioneelId);
        }
        if(filters.panelBerichtId) {
          params.append('Filters.BerichtId', filters.panelBerichtId);
        }
        if (filters.status) {
          // Convert status to array format for API
          // If user is klant and selected status 4 or 5, combine them as [4, 5]
          let statusArray = [];
          if (Array.isArray(filters.status)) {
            statusArray = filters.status;
          } else {
            const statusValue = parseInt(filters.status);
            if (UserService.hasRole(['Klant']) && (statusValue === 4 || statusValue === 5)) {
              statusArray = [4, 5];
            } else {
              statusArray = [statusValue];
            }
          }
          
          // Append each status value to the array parameter
          statusArray.forEach(status => {
            params.append('Filters.Status', status.toString());
          });
        }

        // Add pagination parameters
        params.append('PageNumber', filters.pageNumber || 0);
        params.append('PageCount', filters.pageCount || 50);

        // Add sorting parameters
        if (sorting.field) {
          params.append('SortField', sorting.field);
        }
        if (sorting.direction) {
          params.append('SortDirection', sorting.direction);
        }

        const url = `/Berichten?${params.toString()}`;
        const response = await axiosInstance.get(url);
        
        // Handle new API response format: { records: [...], totalRecords: number, relatiesVerzend: [...], relatiesOntvangt: [...] }
        if (response.data.records && typeof response.data.totalRecords === 'number') {
          setMessages(response.data.records);
          setTotalCount(response.data.totalRecords);
          setRelatiesVerzend(response.data.relatiesVerzend || []);
          setRelatiesOntvangt(response.data.relatiesOntvangt || []);
        } else {
          // Fallback for old format
          setMessages(response.data);
          setTotalCount(response.data.length);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [filters, sorting, axiosInstance]);

  return { messages, loading, error, totalCount, relatiesVerzend, relatiesOntvangt };
};

export default useMessages;